import {createFeatureSelector, createSelector} from "@ngrx/store";
import {AlgorithmicDmvState} from "./algorithmic-dmv.state";
import {AlgorithmicDmvJobInfo} from "../generated-sources/api";

export const ALGORITHMIC_DMV_FEATURE_KEY = "algorithmicDmv";

export const selectAlgorithmicDmvState = createFeatureSelector<AlgorithmicDmvState>(ALGORITHMIC_DMV_FEATURE_KEY);

export const selectJobsForFund = (fundId: number) => createSelector(
    selectAlgorithmicDmvState,
    state => state.jobsByFund[fundId]?.data ?? []
);

const Status = AlgorithmicDmvJobInfo.StatusEnum;

const isActive = (job: AlgorithmicDmvJobInfo): boolean =>
    job.status === Status.New || job.status === Status.InProgress;

const isReadyForReview = (job: AlgorithmicDmvJobInfo): boolean =>
    job.status === Status.Completed && (job.pendingReviewCount ?? 0) > 0;

export const selectActiveJobForFund = (fundId: number) => createSelector(
    selectJobsForFund(fundId),
    (jobs: AlgorithmicDmvJobInfo[]) => jobs.find(isActive)
);

export const selectMostRecentReadyJobForFund = (fundId: number) => createSelector(
    selectJobsForFund(fundId),
    (jobs: AlgorithmicDmvJobInfo[]) => jobs.find(isReadyForReview)
);

export const selectHasActiveJobsForFund = (fundId: number) => createSelector(
    selectActiveJobForFund(fundId),
    job => job !== undefined
);

// Valuation-scoped variants: a run is conceptually one per fund valuation, so the
// status chip and the run dialog reason about a single valuation, not the whole fund.
export const selectActiveJobForFundValuation = (fundId: number, fundValuationId: number) => createSelector(
    selectJobsForFund(fundId),
    (jobs: AlgorithmicDmvJobInfo[]) => jobs.find(j => j.fundValuationId === fundValuationId && isActive(j))
);

export const selectReadyJobForFundValuation = (fundId: number, fundValuationId: number) => createSelector(
    selectJobsForFund(fundId),
    (jobs: AlgorithmicDmvJobInfo[]) => jobs.find(j => j.fundValuationId === fundValuationId && isReadyForReview(j))
);

export const selectActiveDetail = createSelector(
    selectAlgorithmicDmvState,
    state => state.activeDetail.data
);

export const selectActiveDetailStatus = createSelector(
    selectAlgorithmicDmvState,
    state => state.activeDetail.status
);

export const selectIsResultPending = (resultId: number) => createSelector(
    selectAlgorithmicDmvState,
    state => state.pendingActionResultIds.includes(resultId)
);

export const selectIsBulkAccepting = createSelector(
    selectAlgorithmicDmvState,
    state => state.bulkAccepting
);

export const selectGlobalError = createSelector(
    selectAlgorithmicDmvState,
    state => state.error
);
